import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RenderMount from "./RenderMount.jsx";
import DotGrid from "./DotGrid.jsx";
import GridStatus, { RevalidatingOverlay } from "./GridStatus.jsx";
import { SWIMLANE } from "../lib/gridMetrics.js";
import {
  createSurfaceChart,
  computeSurfacePoints,
  computeSurfaceDomain,
  sumLayers,
  drawCanvasDataPoints,
  resolveCanvasColor,
} from "../lib/renderers.js";
import { useQuery, useCurrentRange } from "../lib/dataClient.js";
import { analyticsErrorCopy } from "../lib/analyticsClient.js";
import { useMyPermissions } from "../lib/permissions.js";
import { observeSettledSize } from "../lib/settledResize.js";
import { toApiRange } from "../lib/shell.js";
import { chartTimeWindow } from "../lib/chartTimeWindow.js";
import { useThemeVersion } from "../lib/theme.js";

// SurfaceChart -- stacked / line activity surface with optional hover-only
// data point dots.
//
// `interactiveDots` enables a transparent overlay canvas that, on hover,
// highlights the two endpoints of the segment under the cursor (the actual
// samples flanking what the eye is currently looking at, so the user can
// distinguish a real bucket from an interpolated arc). Off by default so
// the sidebar's per-row sparklines stay perfectly clean.
export function SurfaceChart({
  series: fullSeries,
  layers: fullLayers = null,
  mode = fullLayers?.length ? "stacked" : "line",
  className = "min-h-[198px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] text-[color:var(--plot-ink)]",
  width = 860,
  height = 260,
  padding = 18,
  stroke,
  fill,
  fillPattern = true,
  fillPatternColor,
  fillPatternSize,
  fillDotRadius,
  fillOpacity = 0.8,
  grid = false,
  domainMin,
  domainMax,
  totalStroke,
  totalStrokeWidth,
  layerDefaults,
  lineHalo = true,
  haloStroke,
  haloWidth,
  strokeWidth,
  minLayerHeight,
  interactiveDots = false,
  // Optional: when present, the hover tooltip shows per-layer values
  // sorted by descending count and a bucket time-range. `range` accepts
  // a preset id ("1M", "5D") or a {from, to} object. `legendLayers`
  // overrides which layers feed the tooltip (defaults to `layers`).
  range = null,
  legendLayers: fullLegendLayers = null,
  // Faint outlines of ancestor-scope totals, drawn behind the main chart
  // so the scoped chart shows how much of each ancestor it is. Each item
  // is `{ key, label?, series, stroke?, strokeOpacity?, lineDash? }`. The
  // first entry is treated as the "primary" reference for y-domain
  // anchoring (chart sized to fit every baseline that's visible).
  referenceSeriesList: fullReferenceSeriesList = null,
  // When true, the STANDING tooltip (the one shown while not hovering, anchored
  // to the latest bucket) collapses to just the bucket header + Total — the
  // per-layer breakdown only appears on real hover. Keeps a many-layer chart
  // (e.g. the signals console scanning every signal) from spilling a tall
  // legend over the page when the user isn't interacting with it.
  compactStandingTooltip = false,
}) {
  // Snapshot the cutoff with the data, so hovering cannot reveal unqueried
  // buckets as the clock advances. Preserve elapsed zero-traffic buckets.
  const timeWindow = useMemo(() => {
    const count = mode === "stacked" && fullLayers?.length
      ? fullLayers.find((layer) => layer.series?.length)?.series.length ?? 0
      : fullSeries?.length ?? 0;
    return chartTimeWindow(toApiRange(range), count);
  }, [fullLayers, fullSeries, mode, range]);
  const { layers, legendLayers, referenceSeriesList, series } = useMemo(() => {
    const clip = (values) => timeWindow
      ? values?.slice(0, timeWindow.visibleCount) : values;
    const clipLayers = (values) => values?.map((layer) => ({ ...layer, series: clip(layer.series) }));
    return {
      layers: clipLayers(fullLayers),
      legendLayers: clipLayers(fullLegendLayers),
      referenceSeriesList: clipLayers(fullReferenceSeriesList),
      series: clip(fullSeries),
    };
  }, [fullLayers, fullLegendLayers, fullReferenceSeriesList, fullSeries, timeWindow]);

  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [hoverIndex, setHoverIndex] = useState(-1);
  // Pixel x of the hovered sample in the chart frame; used to position
  // the HTML tooltip and align the crosshair with the canvas-drawn dots.
  const [hoverX, setHoverX] = useState(0);

  // Track container size for the overlay canvas (RenderMount tracks its
  // own size internally; overlay needs its own measurement so we can
  // size the same canvas underneath). Burst-coalesced to match
  // RenderMount's settled-size rebuild — otherwise a divider drag
  // re-renders every chart block per frame just to track a size the
  // base canvas isn't following yet.
  useEffect(() => {
    if (!interactiveDots) return undefined;
    const node = containerRef.current;
    if (!node) return undefined;
    const update = () => {
      const rect = node.getBoundingClientRect();
      setContainerSize((cur) => {
        const w = Math.max(1, Math.round(rect.width));
        const h = Math.max(1, Math.round(rect.height));
        return cur.width === w && cur.height === h ? cur : { width: w, height: h };
      });
    };
    update();
    return observeSettledSize(node, update);
  }, [interactiveDots]);

  // Compute the same totals the base chart draws so the overlay's
  // dot positions match the rendered curve exactly.
  const totals = useMemo(() => {
    if (mode === "stacked" && layers?.length) return sumLayers(layers);
    return Array.isArray(series) ? series : [];
  }, [mode, layers, series]);

  // Reference-aware y-domain ceiling. Computed here (not inside the
  // renderer) so the hover-overlay's independent domain calculation
  // uses the same ceiling — otherwise the crosshair dots drift away
  // from the curve once the reference raises the y-axis.
  //
  // When a reference is present, pin the ceiling to its max — NOT to
  // max(scope, reference). The reference represents the parent total,
  // which by definition bounds every scoped subset. Anchoring on the
  // reference makes it the outermost line in every honest case; if
  // the scoped line ever clips past it, that's a visible signal of
  // bad data (e.g. top-N truncation in the reference) rather than
  // something the chart silently absorbs by rescaling.
  // Normalised list of reference series with usable shape — drops any
  // empty/null entries so the renderer + tooltip share one source of truth.
  const refList = useMemo(() => {
    if (!Array.isArray(referenceSeriesList)) return [];
    return referenceSeriesList.filter(
      (r) => r && Array.isArray(r.series) && r.series.length > 0,
    );
  }, [referenceSeriesList]);

  const effectiveDomainMax = useMemo(() => {
    if (domainMax != null) return domainMax;
    let fromRef = 0;
    for (const r of refList) {
      const m = Math.max(...r.series, 0);
      if (m > fromRef) fromRef = m;
    }
    if (fromRef > 0) return fromRef;
    const fromTotals = totals.length ? Math.max(...totals, 0) : 0;
    return fromTotals > 0 ? fromTotals : null;
  }, [domainMax, totals, refList]);

  // Effective stroke width used by the overlay's dots — needs to match
  // the base chart's total line so the dot looks like part of the curve.
  const effectiveTotalStrokeWidth = totalStrokeWidth ?? (mode === "stacked" ? 2.5 : strokeWidth ?? 2.5);

  // When the user isn't hovering, default to the most recent bucket so
  // the tooltip acts as a standing legend. The crosshair + flanking
  // dots stay tied to `hoverIndex` (real hover only) so a static default
  // doesn't look like a stuck pointer.
  const defaultIndex = totals.length > 0 ? totals.length - 1 : -1;
  const effectiveIndex = hoverIndex >= 0 ? Math.min(hoverIndex, defaultIndex) : defaultIndex;
  const isDefaultMode = hoverIndex < 0 && effectiveIndex >= 0;

  // Tooltip rows: one per layer at the effective index, sorted by descending
  // value so the top contributors are easiest to scan.
  const tooltipLayers = legendLayers || layers;
  const hoveredLayers = useMemo(() => {
    if (effectiveIndex < 0 || !Array.isArray(tooltipLayers) || tooltipLayers.length === 0) {
      return [];
    }
    return tooltipLayers
      .map((l) => ({
        id:    l.id,
        label: l.label || l.id,
        color: l.color,
        value: Number(l.series?.[effectiveIndex] ?? 0),
      }))
      .filter((l) => Number.isFinite(l.value))
      .sort((a, b) => b.value - a.value);
  }, [effectiveIndex, tooltipLayers]);

  const hoveredBucket = useMemo(() => {
    if (effectiveIndex < 0 || !timeWindow) return null;
    const { bucketMs, fromMs, toMs } = timeWindow;
    const start = new Date(fromMs + effectiveIndex * bucketMs);
    const end = new Date(Math.min(toMs, fromMs + (effectiveIndex + 1) * bucketMs));
    return { start, end };
  }, [effectiveIndex, timeWindow]);

  const hoveredTotal = effectiveIndex >= 0 ? Number(totals?.[effectiveIndex] ?? 0) : 0;
  const hoveredMax = hoveredLayers[0]?.value ?? 0;
  // Show the per-layer breakdown on real hover always; in the standing
  // (not-hovering) state only when not asked to stay compact.
  const showLayerBreakdown = !(compactStandingTooltip && isDefaultMode);
  // One tooltip row per visible baseline at the hovered bucket. Ordered
  // the same way they were passed in so the tooltip's stacking matches the
  // legend chips (parent first → unscoped last reads "from your scope
  // outward").
  const hoveredBaselines = useMemo(() => {
    if (effectiveIndex < 0 || refList.length === 0) return [];
    return refList.map((r) => ({
      key: r.key,
      label: r.label || "Baseline",
      value: Number(r.series?.[effectiveIndex] ?? 0),
      stroke: r.stroke,
      lineDash: r.lineDash || null,
    }));
  }, [effectiveIndex, refList]);
  // Compact charts (e.g. the signals console) pass every series as an equal-rank
  // layer, so chart.jsx routes all-but-the-first through `referenceSeriesList`
  // purely to DRAW them — they then duplicate the dot breakdown as baseline
  // dash rows in the tooltip. Drop those duplicate rows for compact charts; the
  // dot breakdown already lists every layer.
  const visibleBaselines = compactStandingTooltip ? [] : hoveredBaselines;
  // Tooltip sizing scales continuously with the chart's measured size. Each
  // axis drives the dimensions it actually controls so a wide-but-short chart
  // doesn't end up with luxurious vertical spacing it can't afford:
  //   - width  → horizontal pads / widths / column gaps / font / dot
  //   - height → vertical pads / row gaps / bar height / line-height
  // Combined with the 0.8×canvasHeight cap on the tooltip itself, the tooltip
  // stays bounded relative to the chart it lives in instead of growing past
  // the curve it describes.
  //
  // The wT range deliberately holds the floor (10px font) until canvas width
  // hits 800px — that's where the floor min-width (~120px) crosses the 15%
  // canvas-width budget. Below that, anything bigger would feel oversized
  // relative to the chart it's annotating. Above 1600px (where the ceiling
  // 240px min-width hits 15%) the tooltip caps and stops growing.
  const wT = Math.max(0, Math.min(1, (containerSize.width  - 800) / (1600 - 800)));
  const hT = Math.max(0, Math.min(1, (containerSize.height - 260) / (480 -  260)));
  const lerpW = (a, b) => a + (b - a) * wT;
  const lerpH = (a, b) => a + (b - a) * hT;
  const tt = {
    // Text + dot — width-driven so a tall-but-narrow chart doesn't push
    // type up past what the canvas width can naturally accommodate.
    fontSize:    lerpW(10, 14),
    headerSize:  lerpW(9,  12),
    totalSize:   lerpW(11, 16),
    dotSize:     lerpW(5,   8),
    // Horizontal — width-driven
    padX:        lerpW(6,  14),
    minW:        lerpW(120, 240),
    maxW:        lerpW(200, 380),
    totalGap:    lerpW(6,  12),
    dotColW:     lerpW(6,  10),
    colGap:      lerpW(4,  10),
    // Vertical — height-driven
    padY:        lerpH(4,  10),
    blockGap:    lerpH(2,   6),
    rowGap:      lerpH(2,   6),
    stackGap:    lerpH(1,   3),
    headerPadB:  lerpH(2,   5),
    barH:        lerpH(2,   6),
    lineHeight:  lerpH(1.15, 1.22),
  };
  // Hard cap so the tooltip never overwhelms its chart. Anything that would
  // push past 80% of canvas height clips at the bottom — the rows are sorted
  // by descending value so the lowest contributors are the ones to lose.
  // Charts that opt into the compact standing tooltip skip the cap: idle they
  // show total-only (tiny), and on hover the user WANTS the full breakdown, so
  // we let it grow to its natural height (spilling below the chart while
  // hovering is fine) rather than truncating the lowest signals.
  const tooltipMaxH = compactStandingTooltip
    ? null
    : containerSize.height > 0
      ? Math.round(containerSize.height * 0.8)
      : null;

  // Resolve overlay colors once per theme/prop change — not per
  // hover transition. getComputedStyle + resolveCanvasColor was
  // running inside the redraw effect on every bucket the cursor
  // crossed; this moves the read to a memo gated on the theme
  // version + the inputs that actually affect color resolution.
  const themeVersion = useThemeVersion();
  const overlayColors = useMemo(() => {
    if (!interactiveDots) return null;
    if (typeof document === "undefined") return null;
    const styles = getComputedStyle(document.documentElement);
    return {
      fill: resolveCanvasColor(totalStroke || stroke || "currentColor", styles),
      halo: lineHalo
        ? resolveCanvasColor(haloStroke || "var(--plot-bg)", styles)
        : null,
      line: resolveCanvasColor("var(--line-strong)", styles),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactiveDots, totalStroke, stroke, haloStroke, lineHalo, themeVersion]);

  // Repaint the overlay dots when the hover index or container size
  // changes. Cheap (just two arc fills); no need to touch the base canvas.
  useEffect(() => {
    if (!interactiveDots) return;
    const canvas = overlayRef.current;
    if (!canvas) return;
    const w = containerSize.width;
    const h = containerSize.height;
    if (!w || !h) return;
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    if (effectiveIndex < 0 || totals.length === 0 || !overlayColors) return;

    const { min, max } = computeSurfaceDomain({
      series: totals,
      layers: null,
      domainMin: domainMin ?? 0,
      domainMax: effectiveDomainMax,
    });
    const points = computeSurfacePoints(totals, w, h, padding, min, max);

    // Vertical crosshair anchored at the active sample's x. Drawn for
    // both real hover and the default-mode anchor (most recent bucket)
    // so the standing tooltip's match-up with its point is obvious.
    // Default mode uses a lower alpha so it reads as ambient rather
    // than a stuck pointer.
    const anchor = points[effectiveIndex];
    if (anchor) {
      ctx.save();
      ctx.strokeStyle = overlayColors.line;
      ctx.globalAlpha = isDefaultMode ? 0.45 : 0.6;
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 3]);
      ctx.beginPath();
      ctx.moveTo(Math.round(anchor.x) + 0.5, padding);
      ctx.lineTo(Math.round(anchor.x) + 0.5, h - padding);
      ctx.stroke();
      ctx.restore();
    }

    // Flanking dots only on real hover — they signal "the segment under
    // the cursor", which doesn't apply to the default standing tooltip.
    if (!isDefaultMode) {
      const indices = effectiveIndex < points.length - 1
        ? [effectiveIndex, effectiveIndex + 1]
        : [effectiveIndex];
      const flanking = indices.map((i) => points[i]).filter(Boolean);
      drawCanvasDataPoints(ctx, flanking, {
        fill: overlayColors.fill,
        radius: Math.max(3, effectiveTotalStrokeWidth * 1.2),
        haloFill: overlayColors.halo,
        haloRadius: Math.max(5, effectiveTotalStrokeWidth * 1.9),
      });
    }
  }, [
    interactiveDots, hoverIndex, effectiveIndex, isDefaultMode, totals,
    containerSize.width, containerSize.height,
    padding, domainMin, effectiveDomainMax, overlayColors,
    effectiveTotalStrokeWidth,
  ]);

  const handleMove = useCallback((event) => {
    if (!interactiveDots) return;
    const node = containerRef.current;
    if (!node || totals.length === 0) return;
    const rect = node.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const w = rect.width;
    if (w <= 0) return;
    const usable = Math.max(1, w - padding * 2);
    const t = (x - padding) / usable;
    if (t < -0.05 || t > 1.05) {
      setHoverIndex(-1);
      return;
    }
    // Snap to the LEFT sample of the hovered segment so the overlay can
    // light up [idx, idx+1] together as the segment endpoints.
    const last = totals.length - 1;
    const idx = Math.max(0, Math.min(last, Math.floor(t * last)));
    // Pixel x of that sample — anchors both the crosshair and the
    // tooltip so the popup sits where the curve actually is.
    const sampleX = padding + (idx / Math.max(1, last)) * usable;
    setHoverIndex((cur) => (cur === idx ? cur : idx));
    setHoverX(sampleX);
  }, [interactiveDots, padding, totals.length]);

  const handleLeave = useCallback(() => {
    if (!interactiveDots) return;
    setHoverIndex(-1);
  }, [interactiveDots]);

  // Memoize the factory closure so its identity is stable across
  // hover-driven re-renders. RenderMount's useLayoutEffect has
  // factory in its dep list — without this memo, every hoverIndex
  // change creates a new arrow, RenderMount sees a new ref and runs
  // replaceChildren + factory(size) + appendChild, destroying and
  // re-creating the entire base chart canvas on every bucket
  // transition. That was the dominant cost when hovering the
  // Activity Surface (visible in CPU profiles as replaceChildren /
  // appendChild churn).
  const baseFactory = useCallback((size) => {
    const nextWidth = size.width || width;
    const nextHeight = size.height || height;
    return createSurfaceChart({
      mode,
      series,
      layers,
      width: nextWidth,
      height: nextHeight,
      padding,
      stroke,
      fill,
      fillPattern,
      fillPatternColor,
      fillPatternSize,
      fillDotRadius,
      fillOpacity,
      grid,
      domainMin,
      domainMax: effectiveDomainMax,
      totalStroke: totalStroke || stroke,
      totalStrokeWidth,
      layerDefaults,
      lineHalo,
      haloStroke,
      haloWidth,
      strokeWidth,
      minLayerHeight,
      referenceSeriesList: refList,
    });
  }, [
    mode, series, layers, width, height, padding, stroke, fill,
    fillPattern, fillPatternColor, fillPatternSize, fillDotRadius,
    fillOpacity, grid, domainMin, effectiveDomainMax, totalStroke,
    totalStrokeWidth, layerDefaults, lineHalo, haloStroke, haloWidth,
    strokeWidth, minLayerHeight, refList,
  ]);

  const renderMount = (
    <RenderMount
      className={className}
      factory={baseFactory}
    />
  );

  if (!interactiveDots) return renderMount;

  // Anchor the tooltip to the effective sample's x. In default mode
  // (no hover) we synthesise an x at the rightmost bucket so the tooltip
  // sits next to the most recent point. Flip to the left side of the
  // cursor once past the chart's midline so the popup never clips
  // against the right edge.
  const containerW = containerSize.width || 0;
  const usableW = Math.max(1, containerW - padding * 2);
  const last = Math.max(1, totals.length - 1);
  const defaultX = padding + ((effectiveIndex >= 0 ? effectiveIndex : last) / last) * usableW;
  const anchorX = isDefaultMode ? defaultX : hoverX;
  const tooltipOnRight = containerW > 0 && anchorX > containerW / 2;
  const tooltipStyle = {
    left: tooltipOnRight ? "auto" : `${Math.round(anchorX) + 10}px`,
    right: tooltipOnRight ? `${Math.round(containerW - anchorX) + 10}px` : "auto",
    top: `${padding}px`,
    fontSize:     `${tt.fontSize}px`,
    lineHeight:   tt.lineHeight,
    paddingLeft:  `${tt.padX}px`,
    paddingRight: `${tt.padX}px`,
    paddingTop:   `${tt.padY}px`,
    paddingBottom: `${tt.padY}px`,
    minWidth:     `${tt.minW}px`,
    maxWidth:     `${tt.maxW}px`,
    rowGap:       `${tt.blockGap}px`,
    ...(tooltipMaxH ? { maxHeight: `${tooltipMaxH}px`, overflow: "hidden" } : null),
  };

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      {renderMount}
      <canvas
        ref={overlayRef}
        className="pointer-events-none absolute inset-0 h-full w-full"
      />
      {effectiveIndex >= 0 && hoveredLayers.length > 0 ? (
        <div
          className={[
            "pointer-events-none absolute z-[2] grid rounded-[8px] border bg-[color:var(--bg)] transition-opacity",
            isDefaultMode ? "border-[color:var(--line)] opacity-90" : "border-[color:var(--line-strong)]",
          ].join(" ")}
          style={tooltipStyle}
        >
          {hoveredBucket ? (
            <span
              className="tracking-[0.06em] uppercase text-[color:var(--muted-soft)]"
              style={{ fontSize: `${tt.headerSize}px` }}
            >
              {formatBucketRange(hoveredBucket.start, hoveredBucket.end)}
            </span>
          ) : null}
          <div
            className={[
              "grid items-baseline border-b border-[color:var(--line-soft)]",
              visibleBaselines.length > 0 ? "[grid-template-columns:auto_auto]" : "",
            ].join(" ")}
            style={{
              columnGap: `${tt.totalGap}px`,
              paddingBottom: `${tt.headerPadB}px`,
              ...(visibleBaselines.length > 0 ? { rowGap: `${tt.stackGap}px` } : null),
              ...(visibleBaselines.length === 0 ? { display: "flex", justifyContent: "space-between" } : null),
            }}
          >
            <span
              className="tracking-[0.06em] uppercase text-[color:var(--muted)]"
              style={{ fontSize: `${tt.headerSize}px` }}
            >Total</span>
            <span
              className="justify-self-end leading-[1.08] font-medium tabular-nums text-[color:var(--ink)]"
              style={{ fontSize: `${tt.totalSize}px` }}
            >
              {hoveredTotal.toLocaleString()}
            </span>
            {visibleBaselines.map((b) => (
              <BaselineTooltipRow key={b.key} row={b} fontSize={tt.headerSize} />
            ))}
          </div>
          {showLayerBreakdown ? (
          <div className="grid" style={{ rowGap: `${tt.rowGap}px` }}>
            {hoveredLayers.map((row) => {
              const pct = hoveredMax > 0 ? (row.value / hoveredMax) * 100 : 0;
              return (
                <div
                  key={row.id}
                  className={[
                    "grid items-center",
                    row.value === 0 ? "opacity-40" : "",
                  ].join(" ")}
                  style={{
                    columnGap: `${tt.colGap}px`,
                    gridTemplateColumns: `${tt.dotColW}px minmax(0,1fr) auto`,
                  }}
                >
                  <span
                    className="rounded-full"
                    style={{
                      backgroundColor: row.color,
                      width:  `${tt.dotSize}px`,
                      height: `${tt.dotSize}px`,
                    }}
                    aria-hidden="true"
                  />
                  <div className="grid min-w-0" style={{ rowGap: `${tt.stackGap}px` }}>
                    <span className="truncate text-[color:var(--ink)]">{row.label}</span>
                    <div
                      className="w-full rounded-[1px] bg-[color:var(--line-soft)]"
                      style={{ height: `${tt.barH}px` }}
                      aria-hidden="true"
                    >
                      <div
                        className="h-full rounded-[1px]"
                        style={{ width: `${pct}%`, backgroundColor: row.color }}
                      />
                    </div>
                  </div>
                  <span className="tabular-nums text-[color:var(--muted)]">{row.value.toLocaleString()}</span>
                </div>
              );
            })}
          </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// Tooltip row for one baseline at the hovered bucket. Mirrors the
// legend chip's swatch (small dash, optional dotted pattern) so the
// reader can match a tooltip row to its line on the chart.
function BaselineTooltipRow({ row, fontSize }) {
  return (
    <>
      <span
        className="inline-flex min-w-0 items-center gap-[5px] tracking-[0.06em] uppercase text-[color:var(--muted-soft)]"
        style={{ fontSize: `${fontSize}px` }}
      >
        <span
          aria-hidden="true"
          className="inline-block h-[1px] shrink-0"
          style={{
            width: "10px",
            backgroundColor: row.lineDash
              ? "transparent"
              : (row.stroke ? `var(--muted)` : "var(--muted)"),
            backgroundImage: row.lineDash
              ? "linear-gradient(to right, var(--muted) 50%, transparent 50%)"
              : "none",
            backgroundSize: row.lineDash ? "4px 1px" : "auto",
            backgroundRepeat: row.lineDash ? "repeat-x" : "no-repeat",
          }}
        />
        <span className="min-w-0 truncate">{row.label}</span>
      </span>
      <span
        className="justify-self-end leading-[1.08] tabular-nums text-[color:var(--muted)]"
        style={{ fontSize: `${fontSize}px` }}
      >
        {Math.round(row.value).toLocaleString()}
      </span>
    </>
  );
}

function formatBucketRange(start, end) {
  if (!start || !end) return "";
  const ds = new Date(start);
  const de = new Date(end);
  const sameDay = ds.toDateString() === de.toDateString();
  const fmt = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
  const timeFmt = (d) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return sameDay ? `${fmt(ds)} ${timeFmt(ds)}–${timeFmt(de)}` : `${fmt(ds)} → ${fmt(de)}`;
}

// The three coherence flavours the swimlane can compute. `summary` is the
// short clause shown in the hover popover; `description` is the longer
// explanation underneath; `formula` trails in monospace. Parallel form so
// the three options read as one axis (always "vs <reference set>").
const COHERENCE_MODE_OPTIONS = [
  {
    value: "tenant",
    label: "tenant",
    summary: "vs every endpoint in the tenant",
    description:
      "Pairwise Jaccard across all tenant endpoints active in this cell. High shading means many endpoints share the same topic mix in this window.",
    formula: "mean Jaccard(sig_a, sig_b) · peers = entire tenant",
  },
  {
    value: "scoped",
    label: "scoped",
    summary: "vs only the endpoints in this scope",
    description:
      "Pairwise Jaccard among in-scope endpoints. Signatures remain tenant-wide to preserve the comparison. Cells are empty when the scope has too few endpoints.",
    formula: "mean Jaccard(sig_a, sig_b) · peers = in-scope endpoints",
  },
  {
    value: "bundle",
    label: "bundle",
    summary: "vs the other topics in this swimlane",
    description:
      "For each endpoint touching this cell, what fraction of the swimlane's other top-K topics did they also touch in the same bucket. Uniform meaning across scopes.",
    formula: "mean |sig ∩ topK \\ {c}| / (K − 1)",
  },
];

// Coherence mode toolbar — single row above the swimlane canvas. Picker
// only; the description for the focused mode lives in a popover below
// the picker so the toolbar itself stays narrow and immune to width
// scaling. Each option's button anchors its own popover so the popover
// lines up with whichever label the user is hovering.
// Width budget for the hover popover, also used to decide whether to flip
// the popover to right-anchored when it would overflow the viewport.
const COHERENCE_POPOVER_W = 300;

export function CoherenceModeToggle({ value, onChange }) {
  const [hovered, setHovered] = useState(null);
  const meta = hovered
    ? COHERENCE_MODE_OPTIONS.find((m) => m.value === hovered)
    : null;
  // Edge-detect: anchor the popover to the picker container's left edge by
  // default, but flip to right-anchored when the picker sits close enough
  // to the viewport's right edge that a 300px popover would overflow.
  // Measured against the radiogroup container (the popover's offset
  // parent) on every meta change, then on resize via ResizeObserver.
  const radiogroupRef = useRef(null);
  const [popoverAnchor, setPopoverAnchor] = useState('left');
  useEffect(() => {
    if (!meta) return undefined;
    const node = radiogroupRef.current;
    if (!node) return undefined;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      const viewportW = window.innerWidth || document.documentElement.clientWidth || 0;
      // Popover starts at rect.left (left-anchored) and extends rightward
      // by COHERENCE_POPOVER_W. Flip to right-anchored when that overflows.
      // 8px breathing room from the viewport edge avoids hugging the wall.
      setPopoverAnchor(
        rect.left + COHERENCE_POPOVER_W + 8 > viewportW ? 'right' : 'left'
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(document.documentElement);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [meta]);
  return (
    <div className="relative flex h-[24px] min-h-[24px] shrink-0 items-center gap-x-[12px] whitespace-nowrap border border-b-0 border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[12px] text-[10px] leading-[1.1] text-[color:var(--muted)]">
      <span className="shrink-0 text-[11px] capitalize text-[color:var(--muted)]">
        coherence
      </span>
      <span
        ref={radiogroupRef}
        role="radiogroup"
        aria-label="Coherence comparison mode"
        className="relative inline-flex shrink-0 items-center gap-[2px]"
      >
        {COHERENCE_MODE_OPTIONS.map((m) => {
          const active = m.value === value;
          return (
            <button
              key={m.value}
              type="button"
              role="radio"
              aria-checked={active}
              aria-describedby={hovered === m.value ? "coherence-mode-desc" : undefined}
              onClick={() => onChange?.(m.value)}
              onMouseEnter={() => setHovered(m.value)}
              onMouseLeave={() =>
                setHovered((prev) => (prev === m.value ? null : prev))
              }
              onFocus={() => setHovered(m.value)}
              onBlur={() =>
                setHovered((prev) => (prev === m.value ? null : prev))
              }
              disabled={!onChange}
              className={[
                "rounded-[5px] px-[6px] py-[2px] text-[11px] leading-[1.1] capitalize transition-colors",
                active
                  ? "bg-[color-mix(in_srgb,var(--ink)_10%,transparent)] text-[color:var(--ink)]"
                  : "text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                onChange ? "cursor-pointer" : "cursor-default",
              ].join(" ")}
            >
              {m.label}
            </button>
          );
        })}
        {meta ? (
          <div
            id="coherence-mode-desc"
            role="tooltip"
            style={{ width: COHERENCE_POPOVER_W }}
            className={[
              "pointer-events-none absolute top-full z-30 mt-2 whitespace-normal rounded-[10px] border px-[10px] py-[8px] text-[10px] leading-[1.4] shadow-[0_4px_12px_rgba(0,0,0,0.25)]",
              popoverAnchor === 'right' ? "right-0" : "left-0",
              meta.value === value
                ? "border-[color:var(--muted-soft)] bg-[color:var(--plot-bg)] text-[color:var(--muted)]"
                : "border-[color:var(--line)] bg-[color:var(--plot-bg)] text-[color:var(--muted)]"
            ].join(' ')}
          >
            <div className="mb-[3px] text-[color:var(--ink)]">{meta.summary}</div>
            <div className="text-[color:var(--muted)]">{meta.description}</div>
          </div>
        ) : null}
      </span>
    </div>
  );
}

export function SwimlaneLegend({ hoveredCell, className = "" }) {
  const cell = hoveredCell;
  const [queryCell, setQueryCell] = useState(null);
  useEffect(() => {
    if (!cell?.cluster?.id || !(cell.bucketEnd > cell.bucketStart)) {
      setQueryCell(null);
      return undefined;
    }
    const timer = setTimeout(() => setQueryCell(cell), 140);
    return () => clearTimeout(timer);
  }, [cell?.cluster?.id, cell?.bucketStart, cell?.bucketEnd]);

  // Fetch cell co-occurrence on hover so we can show the top other
  // clusters that share sessions with this one in the same bucket.
  // Same endpoint the right-panel CellCoOccurrencePanel uses; the
  // server still has to run multiple analytics queries, so debounce
  // the hover target and keep settled cell results briefly cached.
  const cellRange = useCurrentRange();
  const { data: cellData } = useQuery(
    "getCellScopeClusterMatrix",
    {
      scopePath: [],
      clusterId: queryCell?.cluster?.id ?? null,
      bucketStart: queryCell?.bucketStart ?? 0,
      bucketEnd: queryCell?.bucketEnd ?? 0,
      range: cellRange,
    },
    { enabled: !!queryCell?.cluster?.id && queryCell.bucketEnd > queryCell.bucketStart, staleMs: 60_000 },
  );
  const queryMatchesHover = queryCell && cell
    && String(queryCell.cluster?.id) === String(cell.cluster?.id)
    && queryCell.bucketStart === cell.bucketStart
    && queryCell.bucketEnd === cell.bucketEnd;
  // Pinned column 0 of cellData.clusters is the hovered cluster; the
  // rest are the top co-occurring siblings. Take 2 names, comma-join.
  const coClusters = (queryMatchesHover ? (cellData?.clusters || []) : [])
    .filter((c) => String(c.id) !== String(cell?.cluster?.id))
    .slice(0, 2)
    .map((c) => c.name)
    .filter(Boolean);
  // Fixed single-row height — the inspector side is constrained to one line
  // and truncates on overflow so the legend never grows taller and steals
  // height from the chart canvas.
  return (
    <div
      className={[
        "flex h-[26px] min-h-[26px] items-center gap-x-[14px] overflow-hidden whitespace-nowrap border border-[color:var(--line)] border-t-0 bg-[color:var(--plot-bg)] px-[10px] text-[10px] leading-[1.1] text-[color:var(--muted)]",
        className,
      ].filter(Boolean).join(" ")}
    >
      <div className="flex shrink-0 items-center gap-[6px]">
        <span className="inline-flex items-center gap-[3px]">
          <span className="inline-block h-[6px] w-[6px] rounded-full bg-[color:var(--green)] opacity-30" />
          <span className="inline-block h-[6px] w-[6px] rounded-full bg-[color:var(--green)] opacity-70" />
          <span className="inline-block h-[6px] w-[6px] rounded-full bg-[color:var(--green)]" />
        </span>
        <span>brightness · trace volume</span>
      </div>
      <div className="flex shrink-0 items-center gap-[6px]">
        <span className="inline-flex items-center gap-[3px]">
          <span className="inline-block h-[3px] w-[3px] rounded-full bg-[color:var(--green)]" />
          <span className="inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--green)]" />
          <span className="inline-block h-[8px] w-[8px] rounded-full bg-[color:var(--green)]" />
        </span>
        <span>size · how many endpoints</span>
      </div>
      <div className="flex shrink-0 items-center gap-[6px]">
        <span className="inline-flex overflow-hidden rounded-[1px]">
          <span className="inline-block h-[10px] w-[10px]" style={{ backgroundColor: "var(--green)", opacity: 0.1 }} />
          <span className="inline-block h-[10px] w-[10px]" style={{ backgroundColor: "var(--green)", opacity: 0.35 }} />
          <span className="inline-block h-[10px] w-[10px]" style={{ backgroundColor: "var(--green)", opacity: 0.62 }} />
        </span>
        <span>shade · shared with other rows</span>
      </div>
      <div className="ml-auto min-w-0 overflow-hidden text-right text-[color:var(--ink)]">
        {cell ? (
          <span className="flex items-center justify-end gap-x-[8px] overflow-hidden whitespace-nowrap">
            <span className="shrink-0 font-medium">{cell.cluster?.name}</span>
            <span className="shrink-0 text-[color:var(--muted)]">{formatBucketRange(cell.bucketStart, cell.bucketEnd)}</span>
            <span className="shrink-0 text-[color:var(--muted)]">
              {cell.count} {cell.count === 1 ? "trace" : "traces"} · {cell.breadth} {cell.breadth === 1 ? "endpoint" : "endpoints"}
            </span>
            <span className="shrink-0 text-[color:var(--muted)]">
              {Math.round((cell.coherence || 0) * 100)}% overlap with other rows
            </span>
            {coClusters.length > 0 ? (
              <span className="min-w-0 overflow-hidden text-ellipsis text-[color:var(--muted-soft)]">
                often paired with: {coClusters.join(", ")}
              </span>
            ) : null}
          </span>
        ) : (
          <span className="text-[color:var(--muted-soft)]">hover a cell to inspect</span>
        )}
      </div>
    </div>
  );
}

export function ClusterSwimlane({
  scopePath,
  bucketCount,
  maxRows: maxRowsProp,
  onHoverCluster,
  onHoverCell,
  onClickCell,
  // Fired whenever workerData lands so the parent (ActivityChart) can show
  // banners that depend on the response shape — e.g. the cluster-scope
  // siblings note when the server pivoted the rows to co-occurring siblings.
  onMetaChange,
  expanded = false,
  coherenceMode = "tenant",
  className = "min-h-[198px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] text-[color:var(--plot-ink)]",
}) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [hoverHit, setHoverHit] = useState(null);
  // Set of cluster ids whose sub-rows are visible. Click a cluster label
  // to toggle. Persists across collapse/expand mode changes — expanding a
  // cluster in collapsed view scrolls; expanding the whole panel reveals
  // more vertical room without resetting state.
  const [expandedClusterIds, setExpandedClusterIds] = useState(() => new Set());

  const maxRows = maxRowsProp || (expanded ? SWIMLANE.MAX_ROWS_EXPANDED : SWIMLANE.MAX_ROWS);

  // Bucket count derived from available width, capped at SWIMLANE.MAX_COLS.
  const chartAvailableW = Math.max(0, (size.width || 0) - SWIMLANE.LABEL_W);
  const effectiveBucketCount = (() => {
    if (bucketCount) return bucketCount;
    if (!chartAvailableW) return SWIMLANE.MAX_COLS;
    const fit = Math.floor(chartAvailableW / SWIMLANE.COL_TARGET);
    const snapped = Math.max(3, Math.min(SWIMLANE.MAX_COLS, Math.round(fit / 2) * 2));
    return snapped || 3;
  })();

  const cellW = effectiveBucketCount > 0 ? chartAvailableW / effectiveBucketCount : SWIMLANE.COL_TARGET;
  // Lock row height to ROW_TARGET. Previously cellH grew with cellW up to
  // ROW_MAX, which inflated rows when buckets got wide AND broke the
  // natural-height contract (canvas would overflow the container by ~10px
  // per row). Width-driven row growth wasn't really desirable anyway.
  const cellH = SWIMLANE.ROW_TARGET;
  // When expanded we pad up to the rows that fit the container body so the
  // canvas stays dense and can scroll if content exceeds the viewport. When
  // collapsed we let DotGrid scale rows to fit the aspect-ratio-bounded parent.
  const naturalHeight = Math.round(maxRows * cellH + SWIMLANE.HEADER_H + SWIMLANE.PAD_Y);
  const containerBodyH = Math.max(0, (size.height || naturalHeight) - SWIMLANE.HEADER_H);
  const rowsFittingHeight = expanded
    ? Math.max(maxRows, Math.floor(containerBodyH / Math.max(1, cellH)))
    : maxRows;

  const swimlaneRange = useCurrentRange();
  const { data: workerData, error: workerError, loading: workerLoading } = useQuery(
    "getClusterSwimlaneData",
    { scopePath: scopePath || [], bucketCount: effectiveBucketCount, maxRows, range: swimlaneRange, coherenceMode },
    { enabled: Array.isArray(scopePath) },
  );

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const node = containerRef.current;
    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      const next = { width: Math.max(1, Math.round(rect.width)), height: Math.max(1, Math.round(rect.height)) };
      setSize((cur) => (cur.width === next.width && cur.height === next.height ? cur : next));
    };
    updateSize();
    // Burst-coalesced (O-1996): size feeds cellW/cellFn (DotGrid props,
    // a per-frame redraw) AND effectiveBucketCount — a useQuery param —
    // so per-frame updates could re-key the swimlane data fetch
    // mid-drag. Settled sizes only.
    return observeSettledSize(node, updateSize);
  }, []);

  // Build padded rows + cols for DotGrid.
  const rankedClusters = workerData?.rankedClusters ?? [];
  const matrix = workerData?.matrix ?? [];
  const breadthMatrix = workerData?.breadthMatrix ?? null;
  const coherenceMatrix = workerData?.coherenceMatrix ?? null;
  const globalMax = workerData?.globalMax ?? 0;
  const breadthMax = workerData?.breadthMax ?? 0;
  const minTime = workerData?.minTime ?? 0;
  const maxTime = workerData?.maxTime ?? 0;
  const subRowsByCluster = workerData?.subRowsByCluster ?? {};
  const siblingsOf = workerData?.siblingsOf ?? null;

  // Bubble metadata up to the parent (ActivityChart) in a single effect so
  // an overwrite-style setter doesn't clobber siblings with error or vice
  // versa. The dep list is keyed on primitives we actually care about so
  // the effect only fires when the value semantically changes. The
  // lastSentRef compare adds belt-and-suspenders protection against
  // StrictMode's double-mount and against the parent passing a new
  // function reference each render (which would force the effect to fire
  // even when nothing meaningful changed).
  // Route the worker error through analyticsErrorCopy so an RBAC denial
  // renders role-aware copy (e.g. "You're signed in as SecOps, which
  // can't run raw, row-level queries") instead of leaking the raw gRPC
  // body — `ExecuteQuery 403: {"code":7,"message":"missing permission:
  // …"}` — verbatim to the customer. Never surface String(err.message)
  // for an analytics error.
  const caps = useMyPermissions();
  const errMsg = workerError ? analyticsErrorCopy(workerError, caps).message : null;
  const lastSentMetaRef = useRef(null);
  useEffect(() => {
    if (!onMetaChange) return;
    const last = lastSentMetaRef.current;
    const sib = siblingsOf
      ? { id: siblingsOf.id ?? null, name: siblingsOf.name ?? null }
      : null;
    if (last
        && (last.sib?.id ?? null) === (sib?.id ?? null)
        && (last.sib?.name ?? null) === (sib?.name ?? null)
        && last.err === errMsg) {
      return;
    }
    lastSentMetaRef.current = { sib, err: errMsg };
    onMetaChange({ siblingsOf, error: errMsg });
  }, [onMetaChange, siblingsOf?.id, siblingsOf?.name, errMsg]);

  // Rows: each ranked cluster becomes a "cluster" row; when expanded and the
  // user has toggled the cluster open, its sub-rows follow underneath. Parent
  // rows carry `parentIndex` so cellFn can read the right row of `matrix`;
  // sub-rows carry `subMatrix` so cellFn can look up the per-bucket count
  // directly. Padding fills any remaining height so the grid stays rectangular.
  const rows = useMemo(() => {
    const out = [];
    // Expansion is allowed in both collapsed and expanded panel modes.
    // Collapsed view overflows + scrolls; expanded view has natural room.
    const canExpand = Object.keys(subRowsByCluster).length > 0;
    rankedClusters.forEach((c, i) => {
      const isOpen = expandedClusterIds.has(c.id);
      const subBlock = subRowsByCluster[c.id];
      const hasSubs = canExpand && (subBlock?.rows?.length || 0) > 0;
      out.push({
        kind: "cluster",
        id: c.id,
        name: c.name,
        parentIndex: i,
        expandable: hasSubs,
        expanded: isOpen,
      });
      if (isOpen && hasSubs) {
        const subMax = subBlock.max || 1;
        // Sub-rows match the parent row height — visual hierarchy is carried
        // by the group background tint, the indented label, and the spine
        // alone. No explicit height override.
        for (const sub of subBlock.rows) {
          out.push({
            kind: "sub",
            id: `sub:${c.id}:${sub.id}`,
            name: sub.name,
            parentId: c.id,
            subDimId: sub.id,
            subMatrix: sub.matrix,
            subMax,
            indent: 18,
          });
        }
      }
    });
    while (out.length < rowsFittingHeight) {
      out.push({ id: `__pad_r_${out.length}`, name: "", isPadding: true });
    }
    return out;
  }, [rankedClusters, subRowsByCluster, expandedClusterIds, expanded, rowsFittingHeight]);

  const cols = useMemo(() => {
    const labelStep = Math.max(1, Math.ceil(effectiveBucketCount / 8));
    const timeSpan = Math.max(maxTime - minTime, 1);
    const out = [];
    for (let b = 0; b < effectiveBucketCount; b++) {
      const showLabel = b % labelStep === 0;
      const d = new Date(minTime + (b / effectiveBucketCount) * timeSpan);
      out.push({
        id: `bucket-${b}`,
        name: showLabel && minTime ? `${d.getMonth() + 1}/${d.getDate()}` : "",
      });
    }
    return out;
  }, [effectiveBucketCount, minTime, maxTime]);

  const cellFn = useCallback((ri, ci, row, _col, dims) => {
    // Size radius against the *actually drawn* cell dimensions, not the
    // requested rowH/cellW — DotGrid clamps rows down when the container is
    // shorter than requested, and a radius tied to the un-clamped cellH would
    // overflow into adjacent rows.
    const dCellW = dims?.cellW ?? cellW;
    const dRowH = dims?.rowH ?? cellH;
    const maxRadius = Math.max(1.2, Math.min(SWIMLANE.DOT_MAX_R, Math.min(dRowH, dCellW) * 0.38));
    const minRadius = Math.max(0.8, maxRadius * 0.2);
    const placeholderR = Math.max(1.4, maxRadius * 0.22);

    // Spacer rows (visual padding between sub-row groups and the next
    // cluster) draw nothing. Returning null bails before any cell render.
    if (row.kind === "subSpacer") return null;

    if (row.isPadding) {
      return { placeholder: { r: placeholderR, opacity: 0.22 } };
    }

    // Sub-rows: single per-bucket density normalized against the peak across
    // that cluster's top-K sub-rows (not the parent's globalMax, which is
    // orders of magnitude larger and would collapse the sub-row dots). Dot
    // ceiling stays below the parent's so sub-rows read as subordinate.
    if (row.kind === "sub") {
      const count = row.subMatrix?.[ci] || 0;
      const subPeak = Math.max(row.subMax || 0, 1);
      const subMaxRadius = Math.max(1, maxRadius * 0.78);
      const subMinRadius = Math.max(0.8, subMaxRadius * 0.2);
      if (count === 0) return { placeholder: { r: placeholderR * 0.9, opacity: 0.16 } };
      const alpha = Math.max(0.3, Math.min(1, count / Math.max(subPeak * 0.55, 1)));
      const norm = Math.sqrt(count / subPeak);
      const r = subMinRadius + norm * (subMaxRadius - subMinRadius);
      return { dot: { r, opacity: alpha } };
    }

    // Cluster row: index into the aggregate matrix via parentIndex because
    // interleaved sub-rows shift `ri` out of sync with the ranked-cluster index.
    const parentIdx = row.parentIndex ?? ri;
    const count = matrix[parentIdx]?.[ci] || 0;
    if (count === 0) {
      return { placeholder: { r: placeholderR, opacity: 0.22 } };
    }

    // Bundle returns a fraction in [0, 1] (mean of (sigSize−1)/(K−1)
    // across cell members), so we scale directly to the alpha ceiling —
    // no peak-relative normalization. Bright = coherent, matching the
    // contrast panel's shade=expected convention so the dashboard reads
    // consistently across panels.
    let shade = null;
    if (coherenceMatrix) {
      const coh = Math.min(1, coherenceMatrix[parentIdx]?.[ci] || 0);
      if (coh > 0) shade = { opacity: coh * 0.48 };
    }

    // sqrt-scale alpha against the global peak: keeps rank order (top cells
    // still saturate, low cells stay dim) but spreads the visible mid-range
    // out instead of crushing it under one hot outlier. Linear count/peak
    // collapsed every average cell to alpha ≈ 0.1 in real-data distributions.
    const alpha = Math.max(0.22, Math.min(1, Math.sqrt(count / Math.max(globalMax, 1))));
    let r;
    if (breadthMatrix && breadthMax > 0) {
      const norm = Math.sqrt((breadthMatrix[parentIdx]?.[ci] || 0) / breadthMax);
      r = minRadius + norm * (maxRadius - minRadius);
    } else {
      r = maxRadius * 0.55;
    }
    return { shade, dot: { r, opacity: alpha } };
  }, [matrix, coherenceMatrix, globalMax, breadthMatrix, breadthMax, cellW, cellH]);

  // Hover + click handlers operate on the parent cluster regardless of whether
  // the pointer landed on a cluster row or one of its sub-rows. Sub-rows
  // still register hover highlight via DotGrid's hoveredRowIndex but surface
  // the parent cluster as the active hovered entity so downstream callbacks
  // (co-occurrence panel, etc.) stay coherent.
  const handleHover = useCallback((hit) => {
    setHoverHit(hit || null);
    const row = hit?.ri != null ? rows[hit.ri] : null;
    const parentIdx = row?.kind === "sub"
      ? rankedClusters.findIndex((c) => c.id === row.parentId)
      : row?.parentIndex ?? hit?.ri ?? -1;
    const onRowOrCell = hit && (hit.type === "cell" || hit.type === "row");
    const cluster = onRowOrCell && parentIdx >= 0 ? rankedClusters[parentIdx] : null;
    if (onHoverCluster) onHoverCluster(cluster || null);

    if (!onHoverCell) return;
    if (!hit || hit.type !== "cell" || !cluster) {
      onHoverCell(null);
      return;
    }
    {
      const timeSpan = Math.max(maxTime - minTime, 1);
      const bucketStart = minTime + (hit.ci / effectiveBucketCount) * timeSpan;
      const bucketEnd = minTime + ((hit.ci + 1) / effectiveBucketCount) * timeSpan;
      onHoverCell({
        rowIndex: parentIdx,
        bucketIndex: hit.ci,
        cluster,
        bucketStart,
        bucketEnd,
        count: matrix[parentIdx]?.[hit.ci] ?? 0,
        breadth: breadthMatrix?.[parentIdx]?.[hit.ci] ?? 0,
        coherence: coherenceMatrix?.[parentIdx]?.[hit.ci] ?? 0,
        coOccurrence: workerData?.coOccurrence?.[parentIdx]?.[hit.ci] ?? [],
        siblingScopes: workerData?.siblingScopes?.[parentIdx]?.[hit.ci] ?? [],
        breadthDim: workerData?.breadthDim ?? null,
        selfBreadthValue: workerData?.selfBreadthValue ?? null,
      });
    }
  }, [rows, rankedClusters, matrix, breadthMatrix, coherenceMatrix, workerData, minTime, maxTime, effectiveBucketCount, onHoverCluster, onHoverCell]);

  const handleClick = useCallback((hit) => {
    if (!hit) return;
    const row = hit.ri != null ? rows[hit.ri] : null;
    // Cluster-label click toggles expansion (expanded panel only — collapsed
    // doesn't have room for sub-rows).
    if (hit.type === "row" && row?.kind === "cluster" && row.expandable) {
      setExpandedClusterIds((prev) => {
        const next = new Set(prev);
        if (next.has(row.id)) next.delete(row.id);
        else next.add(row.id);
        return next;
      });
      return;
    }
    if (!onClickCell || hit.type !== "cell") return;
    const parentIdx = row?.kind === "sub"
      ? rankedClusters.findIndex((c) => c.id === row.parentId)
      : row?.parentIndex ?? hit.ri;
    const cluster = parentIdx >= 0 ? rankedClusters[parentIdx] : null;
    if (!cluster) return;
    const timeSpan = Math.max(maxTime - minTime, 1);
    const bucketStart = minTime + (hit.ci / effectiveBucketCount) * timeSpan;
    const bucketEnd = minTime + ((hit.ci + 1) / effectiveBucketCount) * timeSpan;
    onClickCell({
      cluster,
      bucketStart,
      bucketEnd,
      count: matrix[parentIdx]?.[hit.ci] ?? 0,
      breadth: breadthMatrix?.[parentIdx]?.[hit.ci] ?? 0,
      coherence: coherenceMatrix?.[parentIdx]?.[hit.ci] ?? 0,
      breadthDim: workerData?.breadthDim ?? null,
      selfBreadthValue: workerData?.selfBreadthValue ?? null,
    });
  }, [expanded, rows, onClickCell, rankedClusters, matrix, breadthMatrix, coherenceMatrix, workerData, minTime, maxTime, effectiveBucketCount]);

  // Cell tooltip: same fields the legend shows, but in human terms instead
  // of bare numbers. Each value gets a one-line explanation of what it
  // means so the reader doesn't have to remember which encoding goes where.
  // Sub-rows surface their own per-bucket count (no breadth/coherence)
  // since the dot encodes that count directly.
  const cellTooltipFn = useCallback((ri, ci, row) => {
    if (!row || row.isPadding || row.kind === "subSpacer") return null;
    const timeSpan = Math.max(maxTime - minTime, 1);
    const bucketStart = minTime + (ci / effectiveBucketCount) * timeSpan;
    const bucketEnd = minTime + ((ci + 1) / effectiveBucketCount) * timeSpan;
    if (row.kind === "sub") {
      const count = row.subMatrix?.[ci] || 0;
      if (count === 0) return null;
      return (
        <div className="flex flex-col gap-[3px]">
          <div className="truncate font-medium text-[color:var(--ink)]">{row.name}</div>
          <div className="text-[color:var(--muted-soft)]">{formatBucketRange(bucketStart, bucketEnd)}</div>
          <div className="text-[color:var(--muted)]">{count} {count === 1 ? "trace" : "traces"} in this bucket</div>
        </div>
      );
    }
    const parentIdx = row.parentIndex ?? ri;
    const cluster = rankedClusters[parentIdx];
    const count = matrix[parentIdx]?.[ci] || 0;
    if (!cluster || count === 0) return null;
    const breadth = breadthMatrix?.[parentIdx]?.[ci] ?? 0;
    const coherence = coherenceMatrix?.[parentIdx]?.[ci] ?? 0;
    const cohPct = Math.round(coherence * 100);
    // Plain-English read of the coherence value — bundle returns the
    // fraction of other top-K clusters this cell's endpoints also
    // touched in the bucket, which translates cleanly into "shared
    // the workflow with N of the other rows" without using the word
    // "coherence" at all in the tooltip body.
    let coherenceLine;
    if (cohPct >= 67) {
      coherenceLine = "Endpoints here are heavily intertwined with the other rows";
    } else if (cohPct >= 33) {
      coherenceLine = "Endpoints here share some traffic with the other rows";
    } else if (cohPct > 0) {
      coherenceLine = "Endpoints here mostly stay in this row. Neighboring rows are quiet.";
    } else {
      coherenceLine = "Endpoints here don't engage with the other rows in this bucket";
    }
    return (
      <div className="flex flex-col gap-[4px]">
        <div className="truncate font-medium text-[color:var(--ink)]">{cluster.name}</div>
        <div className="text-[color:var(--muted-soft)]">{formatBucketRange(bucketStart, bucketEnd)}</div>
        <div className="text-[color:var(--muted)]">
          <span className="text-[color:var(--ink)]">{count}</span> {count === 1 ? "trace" : "traces"}
          <span className="text-[color:var(--muted-soft)]"> from </span>
          <span className="text-[color:var(--ink)]">{breadth}</span> {breadth === 1 ? "endpoint" : "endpoints"}
        </div>
        <div className="text-[10px] text-[color:var(--muted-soft)] leading-[1.35]">
          {coherenceLine} ({cohPct}%)
        </div>
      </div>
    );
  }, [rows, rankedClusters, matrix, breadthMatrix, coherenceMatrix, minTime, maxTime, effectiveBucketCount]);

  // Status banners (loading, error) render INSIDE the container ref so
  // the containerRef-bound ResizeObserver (mount-once useEffect) always
  // gets a live node to observe. Returning a different tree on the
  // loading path left the canvas div unmounted at first paint — the
  // observer attached to nothing, `size` stayed {0,0}, and when data
  // finally arrived the DotGrid had no usable dimensions.
  const errLabel = errMsg
    ? `Couldn't load swimlane: ${errMsg}`
    : null;
  // Initial fetch with no cached data yet — distinct from "fetched and
  // came back empty" (which falls through to the canvas with all-padding
  // dots + an inline note). Loading is the only time the user shouldn't
  // see the placeholder grid, because the placeholder grid is what the
  // empty-results case looks like and they'd be indistinguishable.
  const isInitialFetch = workerLoading && !workerData;
  // Refetch with prior swimlane data still rendered — typically a range
  // or scope tweak that landed on the page while the user is already
  // pointing at a cell. Overlay tells them the lanes they're reading
  // are about to be replaced.
  const isRevalidating = workerLoading && !!workerData;

  return (
    <div
      style={{
        height: "100%",
        paddingLeft: SWIMLANE.PAD_X,
        paddingRight: SWIMLANE.PAD_X,
        // Slightly more bottom padding than top so the canvas has room
        // below the last row before the legend strip. Matches the natural
        // height computation in ActivityChart.
        paddingTop: 8,
        paddingBottom: 18,
      }}
      className={["relative min-h-0 overflow-hidden", className].filter(Boolean).join(" ")}
    >
      <div ref={containerRef} style={{ height: "100%", width: "100%" }}>
        {errLabel ? (
          <GridStatus state="empty" label={errLabel} />
        ) : isInitialFetch ? (
          <GridStatus state="loading" label="Loading swimlane…" />
        ) : (
        <DotGrid
          rows={rows}
          cols={cols}
          cellW={cellW}
          rowH={cellH}
          labelW={SWIMLANE.LABEL_W}
          headerH={SWIMLANE.HEADER_H}
          colHeaderLines={1}
          rowLabelAlign="left"
          cellFn={cellFn}
          cellTooltipFn={cellTooltipFn}
          hoveredRowIndex={hoverHit && (hoverHit.type === "row" || hoverHit.type === "cell") && !rows[hoverHit.ri]?.isPadding ? hoverHit.ri : -1}
          hoveredColIndex={hoverHit && (hoverHit.type === "col" || hoverHit.type === "cell") ? hoverHit.ci : -1}
          onHover={handleHover}
          onClick={handleClick}
          scrollable
        />
        )}
      </div>
      <RevalidatingOverlay active={isRevalidating} label="Refreshing swimlane…" />
    </div>
  );
}
